/**
 * The pin-icon vocabulary — the maintained map from a caption's icon field
 * to the Tabler glyph a marker draws.
 *
 * The keyword is authored free text (the shared parser stores whatever the
 * block wrote), so this table is a VOCABULARY, not an enum: it names the kinds
 * a place tends to be — where people stay, eat, work, learn, travel through —
 * with the common synonyms models actually write folded onto one glyph. An
 * unknown keyword is not an error and never blanks the pin: every miss lands on
 * the generic map-pin glyph, so a block using a word this table has not learned
 * yet still reads as a place.
 *
 * Lookup normalizes case, surrounding space and internal runs of whitespace
 * only. It deliberately does NOT stem or fuzzy-match: `hotels` landing on the
 * fallback is a one-line fix here, while a stemmer quietly mapping `barber` to
 * `bar` is a wrong glyph nobody reports.
 */

import type { ComponentType } from 'react';
import type { IconProps } from '@tabler/icons-react';
import {
  IconAnchor,
  IconBarbell,
  IconBed,
  IconBeach,
  IconBuildingBank,
  IconBuildingChurch,
  IconBuildingFactory2,
  IconBuildingHospital,
  IconBuildingMonument,
  IconBuildingPavilion,
  IconBuildingSkyscraper,
  IconBuildingWarehouse,
  IconCamera,
  IconCheck,
  IconCoffee,
  IconGlassCocktail,
  IconHome,
  IconMapPin,
  IconMountain,
  IconMovie,
  IconMusic,
  IconParking,
  IconPlane,
  IconSchool,
  IconShoppingBag,
  IconStar,
  IconToolsKitchen2,
  IconTrain,
  IconTrees,
  IconX,
} from '@tabler/icons-react';

export type MapPinIconComponent = ComponentType<IconProps>;

/** Keyword -> glyph. Keys are the normalized form (lowercase, single-spaced);
 *  several keywords sharing a glyph is the point — the vocabulary meets models
 *  where they write, the glyph set stays small enough to stay recognizable. */
const MAP_PIN_ICON_BY_KEYWORD: Readonly<Record<string, MapPinIconComponent>> = {
  hotel: IconBed,
  hostel: IconBed,
  stay: IconBed,
  lodging: IconBed,
  coffee: IconCoffee,
  cafe: IconCoffee,
  café: IconCoffee,
  restaurant: IconToolsKitchen2,
  food: IconToolsKitchen2,
  dining: IconToolsKitchen2,
  bar: IconGlassCocktail,
  pub: IconGlassCocktail,
  drinks: IconGlassCocktail,
  office: IconBuildingSkyscraper,
  hq: IconBuildingSkyscraper,
  headquarters: IconBuildingSkyscraper,
  work: IconBuildingSkyscraper,
  shop: IconShoppingBag,
  store: IconShoppingBag,
  market: IconShoppingBag,
  shopping: IconShoppingBag,
  museum: IconBuildingPavilion,
  gallery: IconBuildingPavilion,
  art: IconBuildingPavilion,
  park: IconTrees,
  garden: IconTrees,
  nature: IconTrees,
  airport: IconPlane,
  flight: IconPlane,
  plane: IconPlane,
  train: IconTrain,
  station: IconTrain,
  rail: IconTrain,
  port: IconAnchor,
  harbor: IconAnchor,
  harbour: IconAnchor,
  ferry: IconAnchor,
  hospital: IconBuildingHospital,
  clinic: IconBuildingHospital,
  medical: IconBuildingHospital,
  school: IconSchool,
  university: IconSchool,
  campus: IconSchool,
  education: IconSchool,
  beach: IconBeach,
  coast: IconBeach,
  mountain: IconMountain,
  hike: IconMountain,
  trail: IconMountain,
  home: IconHome,
  house: IconHome,
  apartment: IconHome,
  residence: IconHome,
  bank: IconBuildingBank,
  finance: IconBuildingBank,
  gym: IconBarbell,
  fitness: IconBarbell,
  music: IconMusic,
  concert: IconMusic,
  venue: IconMusic,
  cinema: IconMovie,
  movie: IconMovie,
  theater: IconMovie,
  theatre: IconMovie,
  church: IconBuildingChurch,
  temple: IconBuildingChurch,
  mosque: IconBuildingChurch,
  synagogue: IconBuildingChurch,
  worship: IconBuildingChurch,
  landmark: IconBuildingMonument,
  monument: IconBuildingMonument,
  viewpoint: IconCamera,
  photo: IconCamera,
  camera: IconCamera,
  parking: IconParking,
  factory: IconBuildingFactory2,
  industrial: IconBuildingFactory2,
  warehouse: IconBuildingWarehouse,
  check: IconCheck,
  x: IconX,
  star: IconStar,
};

export const MAP_PIN_ICON_GLYPH_KEYWORDS: readonly string[] = Object.keys(
  MAP_PIN_ICON_BY_KEYWORD,
);

/** What a pin with no keyword — or a keyword the table has not learned —
 *  draws. A generic place glyph, never a blank: the icon slot is part of the
 *  pin's silhouette, and an empty one reads as a rendering fault. */
const MAP_PIN_ICON_FALLBACK: MapPinIconComponent = IconMapPin;

const WHITESPACE_RUN = /\s+/g;

/** The glyph for one authored keyword. Null and unknown both land on the
 *  fallback, so every pin has an icon and the vocabulary can grow without a
 *  single call site changing. */
export function mapPinIconForKeyword(
  keyword: string | null,
): MapPinIconComponent {
  if (keyword == null) {
    return MAP_PIN_ICON_FALLBACK;
  }

  const normalized = keyword.trim().toLowerCase().replace(WHITESPACE_RUN, ' ');
  return MAP_PIN_ICON_BY_KEYWORD[normalized] ?? MAP_PIN_ICON_FALLBACK;
}

/** Whether a keyword names a glyph of its own — the one question the location
 *  rail asks, because a card leads with a RESOLVED kind and stays quiet for
 *  the fallback: a generic pin beside every row is decoration, not data. */
export function hasMapPinIcon(keyword: string | null): boolean {
  if (keyword == null) {
    return false;
  }

  const normalized = keyword.trim().toLowerCase().replace(WHITESPACE_RUN, ' ');
  return MAP_PIN_ICON_BY_KEYWORD[normalized] != null;
}
